<template>
  <div class="payouts-page">
    <router-link class="breadcrumb" :to="{ name: routes.admin.adminDashboard }">
      <span class="breadcrumb-icon">←</span>
      {{ $adminT('adminAll.back_to_operations_console_6f9440', 'Back to Operations Console') }}
    </router-link>
    <div class="page-header">
      <div>
        <p class="eyebrow">{{ $adminT('adminAll.settlements_ee59e6', 'SETTLEMENTS') }}</p>
        <h1>{{ $adminT('adminAll.organizer_payouts_085a60', 'Organizer Payouts') }}</h1>
        <p class="lede">
          {{ $adminT('adminAll.generate_payout_bundles_download_minimax_pdfs_and_export_p_bc8e63', 'Generate payout bundles, download Minimax PDFs, and export payment XMLs in one place.') }}
        </p>
      </div>
      <div class="summary">
        <span class="summary-count">{{ summaryCount }}</span>
        <span class="summary-label">{{ $adminT('adminAll.records_620218', 'RECORDS') }}</span>
      </div>
    </div>

    <div ref="creationCard" class="creation-card card">
      <div class="creation-header">
        <div>
          <h3>{{ $adminT('adminAll.create_new_payout_3d3e1a', 'Create new payout') }}</h3>
          <p class="helper-text">
            {{ $adminT('adminAll.generate_payout_bundle_for_an_event_or_template_select_an__e75a89', 'Generate payout bundle for an event or template. Select an event to get started.') }}
          </p>
        </div>
      </div>
      <div class="creation-grid">
        <div>
          <label>{{ $adminT('adminAll.event_ad8919', 'Event') }}</label>
          <div class="combo" @keydown.esc="eventSearch.open = false">
            <div class="combo-input">
              <input
                v-model="eventSearch.query"
                type="text"
                :placeholder="$adminT('adminAll.select_event_4deb69', 'Select Event...')"
                @input="onEventSearchInput"
                @focus="openEventSearch"
                @blur="closeEventSearch"
              >
              <span class="chevron-icon"><v-icon size="24">mdi-chevron-down</v-icon></span>
            </div>
            <div v-if="eventSearch.open" class="combo-menu">
              <template v-if="!eventSearch.query.trim()">
                <div class="combo-section">{{ $adminT('adminAll.preporuke_ucitano_razdoblje_zagreb_1a04eb', 'Preporuke · učitano razdoblje (Zagreb)') }}</div>
                <div v-if="recommendedEvents.loading" class="combo-empty">{{ $adminT('adminAll.ucitavanje_preporuka_d4a152', 'Učitavanje preporuka…') }}</div>
                <div v-else-if="recommendedEvents.error" class="combo-empty">{{ adminAllMessage(recommendedEvents.error) }}</div>
                <button
                  v-for="evt in recommendedEvents.items"
                  v-else
                  :key="`recommended-${evt.eventId}`"
                  type="button"
                  class="combo-item"
                  @mousedown.prevent="selectEvent({ id: evt.eventId, title: evt.title })"
                >
                  <span class="combo-item-title">
                    {{ adminAllMessage(evt.title) }}
                    <span class="combo-item-sub">{{ evt.legalName || '—' }} · {{ formatDate(evt.to) }}</span>
                  </span>
                  <span class="combo-item-meta">#{{ evt.eventId }}</span>
                </button>
                <div
                  v-if="!recommendedEvents.loading && !recommendedEvents.error && !recommendedEvents.items.length"
                  class="combo-empty"
                >
                  {{ $adminT('adminAll.nema_preporuka_upisite_naziv_za_rucno_pretrazivanje_e7fe21', 'Nema preporuka. Upišite naziv za ručno pretraživanje…') }}
                </div>
                <button v-if="recommendedEvents.hasMore" type="button" class="combo-item" @mousedown.prevent="openAllRecommendations">{{ $adminT('adminAll.prikazi_sve_preporuke_za_razdoblje_5ec3a6', 'Prikaži sve preporuke za razdoblje') }}</button>
              </template>
              <div v-else-if="eventSearch.loading" class="combo-empty">{{ $adminT('adminAll.searching_1a6a5b', 'Searching…') }}</div>
              <div v-else-if="eventSearch.error" class="combo-empty">{{ adminAllMessage(eventSearch.error) }}</div>
              <button
                v-else-if="eventManualOption"
                type="button"
                class="combo-item"
                @mousedown.prevent="selectEvent(eventManualOption)"
              >
                {{ adminAllMessage(eventManualOption.title) }}
              </button>
              <button
                v-for="evt in eventSearch.results"
                :key="evt.id"
                type="button"
                class="combo-item"
                @mousedown.prevent="selectEvent(evt)"
              >
                <span class="combo-item-title">{{ adminAllMessage(evt.title) }}</span>
                <span class="combo-item-meta">#{{ evt.id }}</span>
              </button>
              <div
                v-if="
                  eventSearch.query.trim() &&
                    !eventSearch.loading &&
                    !eventSearch.error &&
                    !eventSearch.results.length &&
                    !eventManualOption
                "
                class="combo-empty"
              >
                {{ $adminT('adminAll.type_to_search_454832', 'Type to search…') }}
              </div>
            </div>
          </div>
        </div>
        <div>
          <label>{{ $adminT('adminAll.event_template_a59510', 'Event Template') }}</label>
          <div class="combo" @keydown.esc="templateSearch.open = false">
            <div class="combo-input">
              <input
                v-model="templateSearch.query"
                type="text"
                :placeholder="$adminT('adminAll.select_template_b90ce4', 'Select Template...')"
                @input="onTemplateSearchInput"
                @focus="templateSearch.open = true"
                @blur="closeTemplateSearch"
              >
              <span class="chevron-icon"><v-icon size="24">mdi-chevron-down</v-icon></span>
            </div>
            <div v-if="templateSearch.open" class="combo-menu">
              <div v-if="templateSearch.loading" class="combo-empty">{{ $adminT('adminAll.searching_1a6a5b', 'Searching…') }}</div>
              <div v-else-if="templateSearch.error" class="combo-empty">
                {{ adminAllMessage(templateSearch.error) }}
              </div>
              <button
                v-else-if="templateManualOption"
                type="button"
                class="combo-item"
                @mousedown.prevent="selectTemplate(templateManualOption)"
              >
                {{ adminAllMessage(templateManualOption.title) }}
              </button>
              <button
                v-for="tpl in templateSearch.results"
                :key="tpl.id"
                type="button"
                class="combo-item"
                @mousedown.prevent="selectTemplate(tpl)"
              >
                <span class="combo-item-title">{{ adminAllMessage(tpl.title) }}</span>
                <span class="combo-item-meta">#{{ tpl.id }}</span>
              </button>
              <div
                v-if="
                  !templateSearch.loading &&
                    !templateSearch.error &&
                    !templateSearch.results.length &&
                    !templateManualOption
                "
                class="combo-empty"
              >
                {{ $adminT('adminAll.type_to_search_454832', 'Type to search…') }}
              </div>
            </div>
          </div>
        </div>
        <div>
          <label>{{ $adminT('adminAll.from_date_optional_637189', 'From date (optional)') }}</label>
          <input v-model="creationForm.fromDate" type="date" :placeholder="$adminT('adminAll.mm_dd_yyyy_01cb1e', 'mm / dd / yyyy')">
        </div>
        <div>
          <label>{{ $adminT('adminAll.to_date_optional_30c7a5', 'To date (optional)') }}</label>
          <input v-model="creationForm.toDate" type="date" :placeholder="$adminT('adminAll.mm_dd_yyyy_01cb1e', 'mm / dd / yyyy')">
        </div>
        <div>
          <label>{{ $adminT('adminAll.event_work_hours_optional_092579', 'Event work hours (optional)') }}</label>
          <input
            v-model="creationForm.eventWorkHours"
            type="number"
            min="0"
            step="0.5"
            placeholder="0"
          >
          <p class="field-helper">{{ $adminT('adminAll.adds_20_eur_vat_per_hour_as_a_manual_invoice_line_item_d70242', 'Adds 20 EUR + VAT per hour as a manual invoice line item.') }}</p>
        </div>
      </div>
      <div class="creation-divider" />
      <div class="creation-actions">
        <main-button class="" flat :disabled="generatingNew" @click.native="resetCreationForm">
          {{ $adminT('adminAll.clear_719ea3', 'Clear') }}
        </main-button>
        <main-button
          class=""
          outline
          :loading="generatingNew"
          :disabled="generatingNew"
          @click.native="generateNewPayout(false)"
        >
          {{ $adminT('adminAll.preview_calculations_c8656c', 'Preview calculations') }}
        </main-button>
        <main-button
          class=""
          depressed
          :loading="generatingNew"
          :disabled="generatingNew"
          @click.native="generateNewPayout()"
        >
          {{ $adminT('adminAll.generate_invoice_bundle_60546a', 'Generate invoice & bundle') }}
        </main-button>
      </div>
      <div v-if="creationError" class="alert error">{{ adminAllMessage(creationError) }}</div>
      <div v-if="creationSuccess" class="alert success">{{ adminAllMessage(creationSuccess) }}</div>
    </div>

    <div class="settlement-card card">
      <div class="creation-header">
        <div>
          <h3>{{ $adminT('adminAll.doga_aji_za_obracun_238073', 'Događaji za obračun') }}</h3>
          <p class="helper-text">
            {{ $adminT('adminAll.pregledajte_zavrsene_doga_aje_povezane_isplate_i_obracune__0299b4', 'Pregledajte završene događaje, povezane isplate i obračune evidentirane izvan sustava isplata.') }}
          </p>
        </div>
      </div>
      <details class="settlement-help">
        <summary>{{ $adminT('adminAll.kako_citati_ovaj_pregled_1768a2', 'Kako čitati ovaj pregled') }}</summary>
        <p><strong>{{ $adminT('adminAll.kandidati_za_obracun_63bd40', 'Kandidati za obračun') }}</strong> {{ $adminT('adminAll.su_zavrseni_doga_aji_s_prihvatljivom_prodajom_mogu_vec_ima_908b6b', 'su završeni događaji s prihvatljivom prodajom. Mogu već imati povezanu isplatu.') }}</p>
        <p><strong>{{ $adminT('adminAll.zadrzana_evidencija_i_povijest_bd35ea', 'Zadržana evidencija i povijest') }}</strong> {{ $adminT('adminAll.ukljucuje_spremljene_i_uklonjene_oznake_cak_i_nakon_povrat_2b6979', 'uključuje spremljene i uklonjene oznake, čak i nakon povrata, otkazivanja ili brisanja događaja.') }}</p>
        <p><strong>{{ $adminT('adminAll.vanjski_obracun_e04944', 'Vanjski obračun') }}</strong> {{ $adminT('adminAll.je_rucna_evidencija_da_su_obracun_i_racun_izra_eni_izvan_s_5e3483', 'je ručna evidencija da su obračun i račun izrađeni izvan sustava isplata. Unos ne izdaje račun i ne prenosi novac.') }}</p>
        <p>{{ $adminT('adminAll.ovaj_pregled_ne_potvr_uje_uskla_enje_svih_prihoda_razdoblj_7973ef', 'Ovaj pregled ne potvrđuje usklađenje svih prihoda razdoblja.') }}</p>
      </details>
      <div class="filter-row settlement-filters">
        <div class="filter">
          <label for="settlement-from">{{ $adminT('adminAll.zavrsetak_doga_aja_od_3ad1c8', 'Završetak događaja od') }}</label>
          <input
            id="settlement-from"
            v-model="settlementFilters.fromDate"
            type="date"
            :max="settlementFilters.toDate || undefined"
          >
        </div>
        <div class="filter">
          <label for="settlement-to">{{ $adminT('adminAll.zavrsetak_doga_aja_do_a97148', 'Završetak događaja do') }}</label>
          <input
            id="settlement-to"
            v-model="settlementFilters.toDate"
            type="date"
            :min="settlementFilters.fromDate || undefined"
          >
        </div>
        <div class="filter">
          <label for="settlement-population">{{ $adminT('adminAll.popis_doga_aja_1df82a', 'Popis događaja') }}</label>
          <select id="settlement-population" v-model="settlementFilters.population" class="settlement-select" @change="settlementFilters.view = 'all'">
            <option value="eligible">{{ $adminT('adminAll.kandidati_za_obracun_63bd40', 'Kandidati za obračun') }}</option>
            <option value="retained">{{ $adminT('adminAll.zadrzana_evidencija_i_povijest_bd35ea', 'Zadržana evidencija i povijest') }}</option>
          </select>
        </div>
        <div class="filter">
          <label for="settlement-view">{{ $adminT('adminAll.status_obracuna_1b0920', 'Status obračuna') }}</label>
          <select id="settlement-view" v-model="settlementFilters.view" class="settlement-select">
            <option value="all">{{ $adminT('adminAll.svi_statusi_93f773', 'Svi statusi') }}</option>
            <option v-if="settlementFilters.population === 'retained'" value="outside">{{ $adminT('adminAll.evidentiran_vanjski_obracun_a0b3c5', 'Evidentiran vanjski obračun') }}</option>
            <option v-if="settlementFilters.population === 'retained'" value="conflict">{{ $adminT('adminAll.vanjski_obracun_i_aktivna_isplata_09597a', 'Vanjski obračun i aktivna isplata') }}</option>
            <option v-if="settlementFilters.population !== 'retained'" value="open">{{ $adminT('adminAll.bez_aktivne_isplate_i_vanjskog_obracuna_5a7d4d', 'Bez aktivne isplate i vanjskog obračuna') }}</option>
            <option v-if="settlementFilters.population !== 'retained'" value="unsettled">{{ $adminT('adminAll.bez_potvrde_otvorene_isplate_1a9de9', 'Bez potvrde / otvorene isplate') }}</option>
          </select>
        </div>
      </div>
      <div class="settlement-toolbar">
        <p class="helper-text">{{ $adminT('adminAll.razdoblje_se_odnosi_na_zavrsetak_doga_aja_prema_vremenu_u__8758fe', 'Razdoblje se odnosi na završetak događaja, prema vremenu u Zagrebu.') }}</p>
        <div class="actions">
          <main-button
            class="settlement-load-button"
            depressed
            :loading="settlementLoading"
            :disabled="settlementLoading"
            @click.native="loadSettlementEvents()"
          >
            {{ $adminT('adminAll.ucitaj_doga_aje_e0f431', 'Učitaj događaje') }}
          </main-button>
        </div>
      </div>
      <p v-if="settlementLoading" role="status">{{ $adminT('adminAll.ucitavanje_doga_aja_3f46c5', 'Učitavanje događaja…') }}</p>
      <p v-if="settlementLoaded" class="helper-text settlement-results" role="status">{{ formatCalendarDate(loadedSettlementFilters.fromDate) }} – {{ formatCalendarDate(loadedSettlementFilters.toDate) }} {{ $adminT('adminAll.broj_doga_aja_dc2ace', '· Broj događaja:') }} {{ settlementTotal }} {{ $adminT('adminAll.stranica_0bbd61', '· Stranica') }} {{ settlementPage }}</p>
      <p v-if="settlementReadStale" class="alert warning">{{ $adminT('adminAll.evidencija_je_promijenjena_ponovno_ucitajte_doga_aje_prije_e7e350', 'Evidencija je promijenjena. Ponovno učitajte događaje prije nove izmjene.') }} <button type="button" class="link-button" @click="loadSettlementEvents(settlementPage, true)">{{ $adminT('adminAll.ponovno_ucitaj_709c42', 'Ponovno učitaj') }}</button></p>
      <div v-if="settlementError" class="alert error">{{ adminAllMessage(settlementError) }}</div>
      <p v-else-if="settlementLoaded && !visibleSettlementEvents.length && !settlementLoading" class="empty">
        {{ $adminT('adminAll.nema_doga_aja_za_ucitano_razdoblje_c7a01e', 'Nema događaja za učitano razdoblje.') }}
      </p>
      <div v-else-if="visibleSettlementEvents.length" class="table-wrapper settlement-table-wrapper" role="region" :aria-label="$adminT('adminAll.doga_aji_i_statusi_obracuna_52c1d3', 'Događaji i statusi obračuna')" tabindex="0">
        <table class="payouts-table settlement-table">
          <thead>
            <tr>
              <th scope="col" class="settlement-event-col">{{ $adminT('adminAll.doga_aj_161b6b', 'Događaj') }}</th>
              <th scope="col" class="settlement-organizer-col">{{ $adminT('adminAll.organizator_i_pdv_3b567d', 'Organizator i PDV') }}</th>
              <th scope="col" class="settlement-payout-col">{{ $adminT('adminAll.isplate_u_sustavu_41d8fe', 'Isplate u sustavu') }}</th>
              <th scope="col" class="settlement-outside-col">{{ $adminT('adminAll.vanjski_obracun_e04944', 'Vanjski obračun') }}</th>
              <th scope="col" class="settlement-actions-col">{{ $adminT('adminAll.radnje_ef806b', 'Radnje') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="evt in visibleSettlementEvents" :key="evt.eventId">
              <td>
                <div class="cell-title">{{ adminAllMessage(evt.title) }}</div>
                <div class="cell-subtitle">#{{ evt.eventId }}</div>
                <div class="cell-subtitle">{{ formatPeriod(evt.from, evt.to) }}</div>
              </td>
              <td>
                <div>{{ evt.legalName || '—' }}</div>
                <div class="cell-subtitle">{{ formatOrganizationType(evt.organizationType) }}</div>
                <div class="cell-subtitle">{{ formatVat(evt) }}</div>
              </td>
              <td>
                <div v-if="evt.payoutCount">
                  <div>{{ $adminT('adminAll.izdano_981b7c', 'Izdano:') }} {{ evt.issuedPayoutCount }} {{ $adminT('adminAll.placeno_a35a76', '· plaćeno:') }} {{ evt.paidPayoutCount }}</div>
                  <div class="cell-subtitle">{{ $adminT('adminAll.bez_potvrde_placanja_b0529d', 'Bez potvrde plaćanja:') }} {{ evt.issuedPayoutCount - evt.paidPayoutCount }}</div>
                  <div class="cell-subtitle">{{ $adminT('adminAll.na_cekanju_1980b8', 'Na čekanju:') }} {{ evt.pendingPayoutCount }} {{ $adminT('adminAll.neuspjelo_03dc2f', '· neuspjelo:') }} {{ evt.failedPayoutCount }}</div>
                  <div class="cell-subtitle">{{ $adminT('adminAll.posljednji_racun_26f709', 'Posljednji račun:') }} {{ evt.latestPayoutInvoiceNumber || '—' }}</div>
                </div>
                <span v-else class="muted">{{ $adminT('adminAll.nema_povezane_isplate_fa8849', 'Nema povezane isplate') }}</span>
              </td>
              <td>
                <span v-if="evt.settlementConflict" class="pill failed">{{ $adminT('adminAll.sukob_vanjski_obracun_i_aktivna_isplata_598643', 'Sukob: vanjski obračun i aktivna isplata') }}</span>
                <div v-if="evt.settlementStatus === 'SETTLED_OUTSIDE_PAYOUT'">{{ $adminT('adminAll.evidentiran_vanjski_obracun_a0b3c5', 'Evidentiran vanjski obračun') }}</div>
                <div v-else-if="evt.settlementStatus">{{ $adminT('adminAll.naslije_ena_oznaka_70ca5e', 'Naslijeđena oznaka:') }} {{ adminAllLabel(evt.settlementStatus) }} {{ $adminT('adminAll.nije_potvrda_obracuna_a6e66f', '(nije potvrda obračuna)') }}</div>
                <div v-else class="muted">{{ $adminT('adminAll.nije_evidentiran_57ef9d', 'Nije evidentiran') }}</div>
                <div v-if="evt.settlementNote" class="cell-subtitle">{{ evt.settlementNote }}</div>
                <button type="button" class="link-button" :disabled="settlementReadStale || settlementBusy[evt.eventId] || (!evt.settlementStatus && ((loadedSettlementFilters && loadedSettlementFilters.population === 'retained') || evt.issuedPayoutCount > 0 || evt.pendingPayoutCount > 0))" @click="editSettlement(evt)">{{ evt.settlementStatus ? $adminT('adminAll.ispravi_evidenciju_64abc2', 'Ispravi evidenciju') : $adminT('adminAll.evidentiraj_vanjski_obracun_0ae3f4', 'Evidentiraj vanjski obračun') }}</button>
                <button v-if="evt.settlementStatus" type="button" class="link-button" :disabled="settlementReadStale || settlementBusy[evt.eventId]" @click="editSettlement(evt, true)">{{ $adminT('adminAll.ukloni_oznaku_cdf3f2', 'Ukloni oznaku') }}</button>
                <button type="button" class="link-button" @click="showHistory(evt.eventId)">{{ $adminT('adminAll.povijest_7041af', 'Povijest') }}</button>
              </td>
              <td>
                <button type="button" class="settlement-open-button" @click="openEventInPayouts(evt)">
                  {{ $adminT('adminAll.otvori_isplate_3d1f6f', 'Otvori isplate') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="settlementLoaded" class="pager">
        <button type="button" :disabled="settlementLoading || settlementPage <= 1" @click="loadSettlementEvents(settlementPage - 1, true)">{{ $adminT('adminAll.prethodna_5085ac', 'Prethodna') }}</button>
        <button type="button" :disabled="settlementLoading || !settlementHasMore" @click="loadSettlementEvents(settlementPage + 1, true)">{{ $adminT('adminAll.sljedeca_2932e6', 'Sljedeća') }}</button>
      </div>
      <form v-if="settlementEditor" class="card settlement-editor" @submit.prevent="saveSettlement">
        <h3>{{ settlementEditor.clear ? $adminT('adminAll.uklanjanje_oznake_206fa4', 'Uklanjanje oznake') : $adminT('adminAll.potvrda_vanjskog_obracuna_162980', 'Potvrda vanjskog obračuna') }} · {{ settlementEditor.event.title }}</h3>
        <p v-if="!settlementEditor.clear">{{ $adminT('adminAll.potvr_ujem_da_su_obracun_i_racun_izra_eni_izvan_isplata_ev_d7b192', 'Potvrđujem da su obračun i račun izrađeni izvan isplata. Evidencija ne izdaje račun i ne prenosi novac.') }}</p>
        <label v-if="!settlementEditor.clear">{{ $adminT('adminAll.referenca_biljeska_obavezno_0e00a8', 'Referenca / bilješka (obavezno)') }}<textarea v-model="settlementEditor.note" maxlength="1000" required /></label>
        <label v-if="settlementRequiresReason(settlementEditor.event)">{{ $adminT('adminAll.razlog_ispravka_uklanjanja_obavezno_d9a37b', 'Razlog ispravka / uklanjanja (obavezno)') }}<textarea v-model="settlementEditor.reason" maxlength="1000" required /></label>
        <p v-if="settlementEditor.error" class="alert error">{{ adminAllMessage(settlementEditor.error) }}</p>
        <button type="submit" :disabled="settlementReadStale || settlementBusy[settlementEditor.event.eventId]">{{ $adminT('adminAll.potvrdi_i_spremi_51ff8b', 'Potvrdi i spremi') }}</button>
        <button type="button" :disabled="settlementReadStale || settlementBusy[settlementEditor.event.eventId]" @click="settlementEditor = null">{{ $adminT('adminAll.odustani_6ef50e', 'Odustani') }}</button>
        <button type="button" :disabled="settlementBusy[settlementEditor.event.eventId]" @click="settlementEditor = null; loadSettlementEvents(settlementPage, true)">{{ $adminT('adminAll.ponovno_ucitaj_zapis_7b8f10', 'Ponovno učitaj zapis') }}</button>
      </form>
      <div v-if="history" class="card settlement-history">
        <h3>{{ $adminT('adminAll.povijest_ee7db3', 'Povijest ·') }} {{ history.eventId ? $adminT('adminAll.doga_aj_value1_7e6d08', 'događaj #\u007b\u007bvalue1\u007d\u007d', { value1: history.eventId }) : $adminT('adminAll.isplata_value1_94f1de', 'isplata #\u007b\u007bvalue1\u007d\u007d', { value1: history.payoutId }) }}</h3>
        <button type="button" @click="history = null">{{ $adminT('adminAll.zatvori_83848b', 'Zatvori') }}</button>
        <p v-if="history.loading">{{ $adminT('adminAll.ucitavanje_2f00f2', 'Učitavanje…') }}</p>
        <p v-else-if="history.error" class="alert error">{{ adminAllMessage(history.error) }}</p>
        <p v-else-if="!history.items.length">{{ $adminT('adminAll.nema_evidentiranih_promjena_b45314', 'Nema evidentiranih promjena.') }}</p>
        <details v-for="entry in history.items" :key="entry.id">
          <summary>{{ formatDate(entry.createdAt) }} · {{ adminAllLabel(entry.action) }} · {{ entry.actorName || $adminT('adminAll.korisnik_value1_19f0ab', 'korisnik #\u007b\u007bvalue1\u007d\u007d', { value1: entry.actorUserId }) }} · {{ entry.reason }}</summary>
          <pre>{{ JSON.stringify({ prije: entry.beforeState, poslije: entry.afterState }, null, 2) }}</pre>
        </details>
        <p v-if="history.items.length === 50">{{ $adminT('adminAll.prikazano_posljednjih_50_promjena_0807d8', 'Prikazano posljednjih 50 promjena.') }}</p>
      </div>
    </div>

    <div ref="payoutsSection">
      <p v-if="associationEventId" class="helper-text">{{ $adminT('adminAll.povezane_isplate_za_doga_aj_d7fb0a', 'Povezane isplate za događaj #') }}{{ associationEventId }} {{ $adminT('adminAll.ukljucujuci_predloske_e6e755', '(uključujući predloške).') }} <button type="button" class="link-button" @click="resetFilters">{{ $adminT('adminAll.ukloni_filtar_f573c2', 'Ukloni filtar') }}</button></p>
      <div class="filters card">
        <div class="filter-row">
          <div class="filter">
            <input
              v-model="filters.orgName"
              type="text"
              :placeholder="$adminT('adminAll.search_by_organizer_2fe08f', 'Search by organizer')"
              @keyup.enter="loadPayouts()"
            >
          </div>
          <div class="filter">
            <input
              v-model="filters.eventTitle"
              type="text"
              :placeholder="$adminT('adminAll.search_by_event_name_or_id_s_72774f', 'Search by event name or ID(s)')"
              @input="associationEventId = null"
              @keyup.enter="loadPayouts()"
            >
          </div>
          <div class="filter">
            <input
              v-model="filters.eventTemplateTitle"
              type="text"
              :placeholder="$adminT('adminAll.search_by_template_125673', 'Search by template')"
              @keyup.enter="loadPayouts()"
            >
          </div>
          <div class="filter date-filter">
            <input
              v-model="filters.fromDate"
              type="date"
              :placeholder="$adminT('adminAll.mm_dd_yyyy_01cb1e', 'mm / dd / yyyy')"
              :max="filters.toDate || undefined"
            >
          </div>
          <div class="filter date-filter">
            <input
              v-model="filters.toDate"
              type="date"
              :placeholder="$adminT('adminAll.mm_dd_yyyy_01cb1e', 'mm / dd / yyyy')"
              :min="filters.fromDate || undefined"
            >
          </div>
        </div>
        <div class="filter-row secondary-row">
          <div class="filter wide">
            <input
              v-model="localSearch"
              type="text"
              :placeholder="$adminT('adminAll.filter_loaded_results_by_organization_event_invoice_number_c2f987', 'Filter loaded results by organization, event, invoice number...')"
            >
          </div>
          <div class="actions">
            <main-button class="" flat :loading="loading" @click.native="loadPayouts()">
              {{ $adminT('adminAll.search_bce064', 'Search') }}
            </main-button>
            <main-button class="" flat @click.native="resetFilters">{{ $adminT('adminAll.reset_44c57a', 'Reset') }}</main-button>
          </div>
        </div>
      </div>
      <p v-if="payoutReadStale" class="alert warning">{{ $adminT('adminAll.evidencija_je_promijenjena_ponovno_ucitajte_isplate_prije__886298', 'Evidencija je promijenjena. Ponovno učitajte isplate prije nove oznake plaćanja.') }} <button type="button" class="link-button" @click="loadPayouts()">{{ $adminT('adminAll.ponovno_ucitaj_709c42', 'Ponovno učitaj') }}</button></p>
      <div v-if="error" class="alert error">{{ adminAllMessage(error) }}</div>
      <div v-if="paymentXmlWarning && !loading" class="alert warning">{{ buildPaymentXmlExcludedMessage(paymentXmlWarning) }}</div>
      <div v-else-if="loading" class="table-wrapper" aria-hidden="true">
        <table class="payouts-table payouts-table--loading">
          <thead>
            <tr>
              <th v-for="column in 12" :key="`loading-head-${column}`">
                <div class="table-skeleton-line table-skeleton-line--header" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in skeletonRows" :key="`loading-row-${row}`">
              <td v-for="column in 12" :key="`loading-row-${row}-${column}`">
                <div class="table-skeleton-line" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else-if="!visiblePayouts.length" class="empty">
        {{ $adminT('adminAll.no_payouts_found_for_the_current_filters_33abe6', 'No payouts found for the current filters.') }}
      </p>
      <div v-else class="results-panel">
        <div class="table-wrapper">
          <table class="payouts-table">
            <thead>
              <tr>
                <th class="checkbox-col">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    @change="toggleAll($event)"
                  >
                </th>
                <th>{{ $adminT('adminAll.created_accf40', 'Created') }}</th>
                <th>{{ $adminT('adminAll.event_template_0db715', 'Event / Template') }}</th>
                <th>{{ $adminT('adminAll.status_bae7d5', 'Status') }}</th>
                <th class="align-center">{{ $adminT('adminAll.invoice_51d860', 'Invoice #') }}</th>
                <th class="align-right">{{ $adminT('adminAll.sold_95286b', 'Sold ($)') }}</th>
                <th class="align-right">{{ $adminT('adminAll.payout_a7000a', 'Payout ($)') }}</th>
                <th class="align-right">{{ $adminT('adminAll.net_871ec8', 'Net ($)') }}</th>
                <th class="align-right">{{ $adminT('adminAll.gross_77069b', 'Gross ($)') }}</th>
                <th>{{ $adminT('adminAll.period_170a28', 'Period') }}</th>
                <th>{{ $adminT('adminAll.issued_date_0e49b0', 'Issued date') }}</th>
                <th>{{ $adminT('adminAll.paid_dc9d45', 'Paid') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payout in visiblePayouts" :key="payout.id">
                <td class="checkbox-col">
                  <input
                    type="checkbox"
                    :value="payout.id"
                    :checked="selectedIds.includes(payout.id)"
                    :title="
                      !isPaymentEligible(payout)
                        ? $adminT('adminAll.invoice_payment_only_excluded_from_payout_xml_003262', 'Invoice payment only - excluded from payout XML.')
                        : ''
                    "
                    @change="toggleSelection(payout.id)"
                  >
                </td>
                <td class="muted">{{ formatDate(payout.createdAt) }}</td>
                <td>
                  <div class="cell-title">{{ getEventTitle(payout) }}</div>
                  <div class="cell-subtitle">{{ getOrgName(payout) }}</div>
                </td>
                <td>
                  <span class="pill" :class="statusClass(payout.status)">{{
                    formatStatus(payout.status)
                  }}</span>
                </td>
                <td class="align-center">{{ payout.minimaxInvoiceNumber || '—' }}</td>
                <td class="align-right muted">{{ formatAmount(getTotalSoldAmount(payout)) }}</td>
                <td class="align-right" :class="{ emphasis: isDraftOrEstimate(payout.status) }">
                  {{ formatAmount(getOrganizerNetAmount(payout)) }}
                </td>
                <td class="align-right" :class="{ emphasis: isDraftOrEstimate(payout.status) }">
                  {{ formatAmount(payout.netAmount) }}
                </td>
                <td class="align-right muted">{{ formatAmount(payout.grossAmount) }}</td>
                <td>{{ formatPeriod(payout.periodFrom, payout.periodTo, payout.periodLabel) }}</td>
                <td class="muted">{{ formatDate(payout.issuedAt) }}</td>
                <td class="nowrap">
                  <div v-if="payout.paidAt" class="paid-cell">
                    <span class="pill paid">{{ $adminT('adminAll.placeno_1c85c8', 'Plaćeno') }} {{ formatDate(payout.paidAt) }}</span>
                  </div>
                  <main-button
                    v-else-if="isIssued(payout)"
                    class="row-action-button"
                    flat
                    :loading="payingIds[payout.id]"
                    :disabled="payoutReadStale || payingIds[payout.id]"
                    @click.native="setPayoutPaid(payout)"
                  >
                    {{ $adminT('adminAll.evidentiraj_placanje_346771', 'Evidentiraj plaćanje') }}
                  </main-button>
                  <span v-else class="muted">—</span>
                  <button type="button" class="link-button" @click="showHistory(null, payout.id)">{{ $adminT('adminAll.povijest_7041af', 'Povijest') }}</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="table-footer">
          <div class="table-footer-top">
            <div v-if="totalPages > 1 && !localSearch.trim()" class="pager">
              <main-button
                class="pager-button"
                flat
                :disabled="loading || page <= 1"
                @click.native="goToPrevPage"
              >
                {{ $adminT('adminAll.prev_e96fea', 'Prev') }}
              </main-button>
              <span class="pager-text">{{ $adminT('adminAll.page_fb0627', 'Page') }} {{ page }} / {{ totalPages }}</span>
              <main-button
                class="pager-button"
                flat
                :disabled="loading || page >= totalPages"
                @click.native="goToNextPage"
              >
                {{ $adminT('adminAll.next_bc9819', 'Next') }}
              </main-button>
            </div>
            <span class="selection-hint">{{
              selectedIds.length ? $adminT('adminAll.value1_payout_s_selected_dcc470', '\u007b\u007bvalue1\u007d\u007d payout(s) selected', { value1: selectedIds.length }) : $adminT('adminAll.no_payouts_selected_2c99ec', 'No payouts selected')
            }}</span>
          </div>
          <div class="footer-actions">
            <div class="email-send-panel">
              <input
                v-model="sendRecipientEmail"
                type="email"
                :placeholder="$adminT('adminAll.organizer_email_474f6b', 'Organizer email')"
                :disabled="!downloadableSelectedIds.length || !!selectedSendOrganizationError || sendingDocs"
              >
              <main-button
                class="footer-action-button"
                flat
                :disabled="!canSendSelectedPayoutDocs || sendingDocs"
                :loading="sendingDocs"
                @click.native="sendSelectedPayoutDocs"
              >
                {{ $adminT('adminAll.send_docs_by_email_807c43', 'Send docs by email') }}
              </main-button>
            </div>
            <main-button
              class="footer-action-button"
              flat
              :disabled="!paymentEligibleSelectedIds.length || generating"
              :loading="generating"
              @click.native="generatePaymentXml"
            >
              {{ $adminT('adminAll.generate_payment_xml_058ba0', 'Generate payment XML') }}
            </main-button>
            <main-button
              class="footer-action-button"
              flat
              :disabled="!downloadableSelectedIds.length || generating"
              :loading="generating"
              @click.native="downloadSelectedPayoutDocs"
            >
              {{ $adminT('adminAll.download_payout_docs_1c4ae5', 'Download payout docs') }}
            </main-button>
          </div>
        </div>
        <div v-if="selectedSendOrganizationError" class="alert warning footer-alert">
          {{ adminAllMessage(selectedSendOrganizationError) }}
        </div>
        <div v-if="sendSuccess" class="alert success footer-alert">{{ adminAllMessage(sendSuccess) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import {
  GET_ALL_ORGANIZER_PAYOUTS_PAGE,
  GET_SETTLEMENT_EVENTS,
  GET_SETTLEMENT_HISTORY,
  SET_EVENT_SETTLEMENT_STATUS,
  SET_ORGANIZER_PAYOUT_PAID,
} from '@/graphql/payout.graphql'
import { SEARCH_EVENTS_SUPERADMIN } from '@/graphql/event.graphql'
import { SEARCH_EVENT_TEMPLATES_SUPERADMIN } from '@/graphql/eventTemplate.graphql'
import {
  downloadEventPayoutDocuments,
  downloadEventTemplatePayoutDocuments,
  downloadOrganizerPayoutDocuments,
  downloadOrganizerPayoutPaymentXml,
  sendOrganizerPayoutDocumentsByEmail,
} from '@/http'
import moment from 'moment-timezone'

const PRINT_SCROLL_SELECTOR = '.table-wrapper'
export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'organizer-payouts-admin',
  data() {
    return {
      payouts: [],
      loading: false,
      error: null,
      paymentXmlWarning: null,
      creationError: null,
      creationSuccess: null,
      generatingNew: false,
      creationForm: {
        eventId: null,
        eventTemplateId: null,
        fromDate: '',
        toDate: '',
        eventWorkHours: '',
        createInvoice: true,
      },
      localSearch: '',
      filters: {
        orgName: '',
        eventTitle: '',
        eventTemplateTitle: '',
        fromDate: '',
        toDate: '',
      },
      selectedIds: [],
      generating: false,
      sendingDocs: false,
      sendRecipientEmail: '',
      sendSuccess: null,
      downloadingId: null,
      payingIds: {},
      payoutRequestId: 0,
      loadedPayoutFilterKey: null,
      payoutReadStale: false,
      settlementReadStale: false,
      settlementRequestId: 0,
      recommendationRequestId: 0,
      settlementPage: 1,
      settlementPageSize: 50,
      settlementTotal: 0,
      settlementHasMore: false,
      settlementAsOf: null,
      loadedSettlementFilters: null,
      settlementBusy: {},
      settlementEditor: null,
      history: null,
      associationEventId: null,
      settlementFilters: {
        fromDate: moment.tz('Europe/Zagreb').subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        toDate: moment.tz('Europe/Zagreb').format('YYYY-MM-DD'),
        view: 'all',
        population: 'eligible',
      },
      settlementEvents: [],
      settlementLoading: false,
      settlementLoaded: false,
      settlementError: null,
      recommendedEvents: {
        items: [],
        hasMore: false,
        loading: false,
        loaded: false,
        error: null,
      },
      eventSearch: {
        query: '',
        results: [],
        loading: false,
        error: null,
        open: false,
        requestId: 0,
        timer: null,
      },
      templateSearch: {
        query: '',
        results: [],
        loading: false,
        error: null,
        open: false,
        requestId: 0,
        timer: null,
      },
      eventSearchCache: {},
      templateSearchCache: {},
      page: 1,
      pageSize: 25,
      totalCount: 0,
      printScrollPositions: [],
      beforePrintHandler: null,
      afterPrintHandler: null,
      printMediaQuery: null,
      printMediaQueryHandler: null,
    }
  },
  computed: {
    visibleSettlementEvents() {
      return this.settlementEvents
    },
    allSelected() {
      return this.visiblePayouts.length && this.selectedIds.length === this.visiblePayouts.length
    },
    totalPages() {
      return this.totalCount ? Math.max(1, Math.ceil(this.totalCount / this.pageSize)) : 1
    },
    skeletonRows() {
      return 8
    },
    summaryCount() {
      if (this.localSearch.trim()) return this.visiblePayouts.length

      return this.totalCount || this.visiblePayouts.length
    },
	    visiblePayouts() {
	      if (!this.localSearch.trim()) {
	        return this.payouts
	      }
      const needle = this.localSearch.toLowerCase()

	      return this.payouts.filter(p => {
        const eventTitle = this.getEventTitle(p).toLowerCase()
        const templateTitle = this.getTemplateTitle(p).toLowerCase()
        const orgName = this.getOrgName(p).toLowerCase()
        const invoiceNumber = (p.minimaxInvoiceNumber || '').toLowerCase()

        return (
          eventTitle.includes(needle) ||
          templateTitle.includes(needle) ||
          orgName.includes(needle) ||
          invoiceNumber.includes(needle)
        )
	      })
	    },
    paymentEligibleSelectedIds() {
      return this.selectedIds.filter(id => {
        const payout = this.payouts.find(p => p.id === id)

        return payout ? this.isPaymentEligible(payout) : false
      })
    },
    selectedDownloadablePayouts() {
      return this.downloadableSelectedIds
        .map(id => this.payouts.find(payout => payout.id === id))
        .filter(Boolean)
    },
    selectedSendOrganizationContext() {
      if (!this.selectedDownloadablePayouts.length) return null
      const organizationIds = Array.from(
        new Set(
          this.selectedDownloadablePayouts
            .map(payout => Number(payout && payout.organization && payout.organization.id))
            .filter(Boolean),
        ),
      )
      if (organizationIds.length !== 1) return null

      const organization = this.selectedDownloadablePayouts[0].organization || {}

      return {
        id: organizationIds[0],
        legalName: organization.legalName || this.$adminT('adminAll.organizer_debd17', 'Organizer'),
        email: organization.email || '',
      }
    },
    selectedSendOrganizationError() {
      if (!this.selectedDownloadablePayouts.length) return null
      const organizationIds = Array.from(
        new Set(
          this.selectedDownloadablePayouts
            .map(payout => Number(payout && payout.organization && payout.organization.id))
            .filter(Boolean),
        ),
      )
      if (organizationIds.length > 1) {
        return this.$adminT('adminAll.select_issued_payouts_for_a_single_organizer_before_sendin_cfc438', 'Select issued payouts for a single organizer before sending documents.')
      }

      return null
    },
    canSendSelectedPayoutDocs() {
      return Boolean(
        this.downloadableSelectedIds.length &&
          !this.selectedSendOrganizationError &&
          (this.sendRecipientEmail || '').trim(),
      )
    },
	    downloadableSelectedIds() {
	      return this.selectedIds.filter(id => {
	        const payout = this.payouts.find(p => p.id === id)
	        if (!payout) return false

        return String(payout.status || '').toLowerCase() === 'issued'
	      })
	    },
	    eventManualOption() {
	      const term = (this.eventSearch.query || '').trim()
	      if (!term) return null
      if (!/^[0-9]+$/.test(term)) return null
      const id = Number(term)
      if (!Number.isFinite(id) || id <= 0) return null
      const already = this.eventSearch.results.some(e => Number(e.id) === id)
      if (already) return null

      return { id, manualOption: true, title: this.$adminT('adminAll.use_event_value1_9ff917', 'Use Event #{{value1}}', { value1: id }) }
    },
    templateManualOption() {
      const term = (this.templateSearch.query || '').trim()
      if (!term) return null
      if (!/^[0-9]+$/.test(term)) return null
      const id = Number(term)
      if (!Number.isFinite(id) || id <= 0) return null
      const already = this.templateSearch.results.some(t => Number(t.id) === id)
      if (already) return null

      return { id, manualOption: true, title: this.$adminT('adminAll.use_template_value1_745c35', 'Use Template #{{value1}}', { value1: id }) }
    },
  },
  watch: {
    selectedIds() {
      this.syncSendRecipientEmail()
      this.sendSuccess = null
    },
    payouts() {
      this.syncSendRecipientEmail()
    },
  },
  created() {
    this.loadPayouts()
  },
  mounted() {
    if (typeof document !== 'undefined') {
      document.body.classList.add('admin-report-print-layout')
    }
    this.setupPrintHandlers()
  },
  beforeDestroy() {
    this.cleanupPrintHandlers()
    if (typeof document !== 'undefined') {
      document.body.classList.remove('admin-report-print-layout')
    }
  },
  methods: {
    getPrintScrollContainers() {
      if (typeof document === 'undefined') {
        return []
      }

      return Array.from(document.querySelectorAll(PRINT_SCROLL_SELECTOR))
    },
    preparePrintLayout() {
      this.printScrollPositions = this.getPrintScrollContainers().map(container => {
        const position = {
          container,
          top: container.scrollTop,
          left: container.scrollLeft,
        }

        container.scrollTop = 0
        container.scrollLeft = 0

        return position
      })
    },
    restorePrintLayout() {
      this.printScrollPositions.forEach(({ container, top, left }) => {
        if (!container || !container.isConnected) {
          return
        }

        container.scrollTop = top
        container.scrollLeft = left
      })
      this.printScrollPositions = []
    },
    setupPrintHandlers() {
      if (typeof window === 'undefined') {
        return
      }

      this.beforePrintHandler = () => this.preparePrintLayout()
      this.afterPrintHandler = () => this.restorePrintLayout()
      window.addEventListener('beforeprint', this.beforePrintHandler)
      window.addEventListener('afterprint', this.afterPrintHandler)

      if (typeof window.matchMedia !== 'function') {
        return
      }

      this.printMediaQuery = window.matchMedia('print')
      this.printMediaQueryHandler = event => {
        if (event.matches) {
          this.preparePrintLayout()
        } else {
          this.restorePrintLayout()
        }
      }

      if (typeof this.printMediaQuery.addEventListener === 'function') {
        this.printMediaQuery.addEventListener('change', this.printMediaQueryHandler)
      } else if (typeof this.printMediaQuery.addListener === 'function') {
        this.printMediaQuery.addListener(this.printMediaQueryHandler)
      }
    },
    cleanupPrintHandlers() {
      if (typeof window !== 'undefined') {
        if (this.beforePrintHandler) {
          window.removeEventListener('beforeprint', this.beforePrintHandler)
        }
        if (this.afterPrintHandler) {
          window.removeEventListener('afterprint', this.afterPrintHandler)
        }
      }

      if (this.printMediaQuery && this.printMediaQueryHandler) {
        if (typeof this.printMediaQuery.removeEventListener === 'function') {
          this.printMediaQuery.removeEventListener('change', this.printMediaQueryHandler)
        } else if (typeof this.printMediaQuery.removeListener === 'function') {
          this.printMediaQuery.removeListener(this.printMediaQueryHandler)
        }
      }

      this.restorePrintLayout()
      this.beforePrintHandler = null
      this.afterPrintHandler = null
      this.printMediaQuery = null
      this.printMediaQueryHandler = null
    },
    statusClass(status) {
      if (!status) return ''

      return status.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    },
    formatStatus(status) {
      if (!status) return '—'
      // Convert status like "DRAFT_ESTIMATE" to "DRAFT / ESTIMATE"

      return this.adminAllLabel(status.toUpperCase().replace(/_/g, ' / '))
    },
    isDraftOrEstimate(status) {
      if (!status) return false
      const lower = status.toLowerCase()

      return lower.includes('draft') || lower.includes('estimate')
    },
    async loadPayouts(pageOverride = null) {
      const requestId = ++this.payoutRequestId
      this.loading = true
      this.error = null
      try {
        const filter = this.buildFilter()
        if (!filter) {
          return
        }
        const filterKey = JSON.stringify(filter)
        const page = pageOverride != null ? pageOverride : filterKey === this.loadedPayoutFilterKey ? this.page : 1
        const offset = Math.max(0, (page - 1) * this.pageSize)
        const { data } = await this.$apollo.query({
          query: GET_ALL_ORGANIZER_PAYOUTS_PAGE,
          variables: { filter, offset, limit: this.pageSize },
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.payoutRequestId) return
        const payload = (data && data.getAllOrganizerPayoutsPage) || { items: [], totalCount: 0 }
        const validPage = Math.max(1, Math.ceil((payload.totalCount || 0) / this.pageSize))
        if (page > validPage) return this.loadPayouts(validPage)
        this.loadedPayoutFilterKey = filterKey
        this.payoutReadStale = false
        this.payouts = payload.items || []
        this.totalCount = payload.totalCount || 0
        this.page = Math.min(Math.max(1, page), this.totalPages)
        this.selectedIds = []
      } catch (err) {
        const message = (err && err.message) || this.adminAllNotice('Failed to load payouts')
        if (requestId === this.payoutRequestId) this.error = message
      } finally {
        if (requestId === this.payoutRequestId) this.loading = false
      }
    },
    goToPrevPage() {
      if (this.loading) return
      if (this.page <= 1) return
      this.loadPayouts(this.page - 1)
    },
    goToNextPage() {
      if (this.loading) return
      if (this.page >= this.totalPages) return
      this.loadPayouts(this.page + 1)
    },
    onEventSearchInput() {
      this.creationForm.eventId = null
      this.invalidateManualSearch()
      this.creationForm.eventTemplateId = null
      this.templateSearch.query = ''
      this.templateSearch.results = []
      this.templateSearch.error = null

      this.debouncedSearch('event', this.eventSearch.query)
    },
    onTemplateSearchInput() {
      this.creationForm.eventTemplateId = null
      this.invalidateManualSearch()
      this.creationForm.eventId = null
      this.eventSearch.query = ''
      this.eventSearch.results = []
      this.eventSearch.error = null

      this.debouncedSearch('template', this.templateSearch.query)
    },
    openEventSearch() {
      this.eventSearch.open = true
      this.loadRecommendedEvents()
    },
    async loadRecommendedEvents(force = false) {
      if (this.recommendedEvents.loaded && !force) return
      const requestId = ++this.recommendationRequestId
      this.recommendedEvents.loading = true
      this.recommendedEvents.error = null
      try {
        const period = this.loadedSettlementFilters || this.settlementFilters
        const { data } = await this.$apollo.query({
          query: GET_SETTLEMENT_EVENTS,
          variables: { fromDate: period.fromDate, toDate: period.toDate, onlyOpen: true, offset: 0, limit: 20 },
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.recommendationRequestId) return
        const result = data.getSettlementEvents
        this.recommendedEvents.items = result.items
        this.recommendedEvents.hasMore = result.hasMore
        this.recommendedEvents.loaded = true
      } catch (err) {
        if (requestId === this.recommendationRequestId) this.recommendedEvents.error = err.message || this.adminAllNotice('Preporuke nije moguće učitati.')
      } finally {
        if (requestId === this.recommendationRequestId) this.recommendedEvents.loading = false
      }
    },
    async loadSettlementEvents(page = 1, preserve = false) {
      const filters = preserve && this.loadedSettlementFilters ? { ...this.loadedSettlementFilters } : { ...this.settlementFilters }
      if (!moment(filters.fromDate, 'YYYY-MM-DD', true).isValid() ||
          !moment(filters.toDate, 'YYYY-MM-DD', true).isValid() || filters.fromDate > filters.toDate) {
        this.settlementError = this.adminAllNotice('Odaberite valjane datume; početak mora biti prije ili jednak kraju.')

        return
      }
      const requestId = ++this.settlementRequestId
      this.settlementLoading = true
      this.settlementError = null
      try {
        const { data } = await this.$apollo.query({
          query: GET_SETTLEMENT_EVENTS,
          variables: { ...filters, offset: (page - 1) * this.settlementPageSize, limit: this.settlementPageSize,
            asOf: preserve ? this.settlementAsOf : null },
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.settlementRequestId) return
        const result = data.getSettlementEvents
        if (page > 1 && !result.items.length && result.totalCount) {
          return this.loadSettlementEvents(Math.ceil(result.totalCount / this.settlementPageSize), preserve)
        }
        this.settlementReadStale = false
        this.settlementEvents = result.items
        this.settlementTotal = result.totalCount
        this.settlementHasMore = result.hasMore
        this.settlementAsOf = result.asOf
        this.settlementPage = result.totalCount ? page : 1
        this.loadedSettlementFilters = filters
        this.settlementLoaded = true
        this.loadRecommendedEvents(true)
      } catch (err) {
        if (requestId === this.settlementRequestId) this.settlementError = err.message || this.adminAllNotice('Događaje nije moguće učitati.')
      } finally {
        if (requestId === this.settlementRequestId) this.settlementLoading = false
      }
    },
    openAllRecommendations() {
      this.settlementFilters = { ...(this.loadedSettlementFilters || this.settlementFilters), view: 'open', population: 'eligible' }
      this.eventSearch.open = false
      this.loadSettlementEvents()
    },
    editSettlement(evt, clear = false) {
      if (this.settlementReadStale || this.settlementBusy[evt.eventId] || (!evt.settlementStatus && this.loadedSettlementFilters && this.loadedSettlementFilters.population === 'retained')) return
      this.settlementEditor = { event: evt, clear, note: evt.settlementNote || '', reason: '', error: null }
    },
    settlementRequiresReason(evt) {
      return Boolean(evt.settlementStatus || evt.settlementVersion > 0)
    },
    async saveSettlement() {
      const editor = this.settlementEditor
      if (!editor || this.settlementReadStale || this.settlementBusy[editor.event.eventId]) return
      const evt = editor.event
      if ((!editor.clear && (!editor.note.trim() || editor.note.trim().length > 1000)) ||
          (this.settlementRequiresReason(evt) && !editor.reason.trim())) {
        editor.error = this.adminAllNotice('Unesite referencu (1–1000 znakova) i razlog za izmjenu ili brisanje.')

        return
      }
      this.$set(this.settlementBusy, evt.eventId, true)
      editor.error = null
      try {
        await this.$apollo.mutate({ mutation: SET_EVENT_SETTLEMENT_STATUS,
          variables: { eventId: Number(evt.eventId), status: editor.clear ? null : 'SETTLED_OUTSIDE_PAYOUT',
            note: editor.clear ? null : editor.note.trim(), expectedVersion: evt.settlementVersion,
            reason: editor.reason.trim() || null } })
        this.settlementEditor = null
        await this.refreshSettlementRecords()
      } catch (err) {
        editor.error = [err.message || this.adminAllNotice('Spremanje nije uspjelo.'), this.adminAllNotice(' Ponovno učitajte aktualni zapis prije nove izmjene.')]
      } finally {
        this.$delete(this.settlementBusy, evt.eventId)
      }
    },
    async refreshSettlementRecords() {
      this.payoutReadStale = true
      this.settlementReadStale = true
      this.settlementAsOf = null
      await Promise.all([this.loadPayouts(), this.loadedSettlementFilters
        ? this.loadSettlementEvents(this.settlementPage, true) : this.loadRecommendedEvents(true)])
    },
    async showHistory(eventId = null, payoutId = null) {
      const history = { eventId, payoutId, loading: true, error: null, items: [] }
      this.history = history
      try {
        const { data } = await this.$apollo.query({ query: GET_SETTLEMENT_HISTORY,
          variables: { eventId: eventId == null ? null : Number(eventId), payoutId: payoutId == null ? null : Number(payoutId), limit: 50 },
          fetchPolicy: 'network-only' })
        history.items = data.getSettlementHistory
      } catch (err) {
        history.error = err.message || this.adminAllNotice('Povijest nije moguće učitati.')
      } finally {
        history.loading = false
      }
    },
    openEventInPayouts(evt) {
      this.selectEvent({ id: evt.eventId, title: evt.title })
      this.creationError = null
      this.creationSuccess = null
      this.filters = {
        ...this.filters,
        orgName: '',
        eventTitle: '',
        eventTemplateTitle: '',
        fromDate: '',
        toDate: '',
      }
      this.associationEventId = Number(evt.eventId)
      this.localSearch = ''
      this.page = 1
      this.loadPayouts(1)
      this.$nextTick(() => {
        const target = this.$refs.creationCard
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    formatCalendarDate(value) {
      return moment(value, 'YYYY-MM-DD', true).format('DD.MM.YYYY')
    },
    formatOrganizationType(type) {
      if (!type) return '—'

      return String(type).replace(/_/g, ' ').toLowerCase()
    },
    formatVat(evt) {
      if (evt.vatObligor == null && evt.vatPercentage == null) return '—'
      const obligor = evt.vatObligor == null ? this.$adminT('adminAll.pdv_status_nepoznat_f12c00', 'PDV status nepoznat') : evt.vatObligor ? this.$adminT('adminAll.obveznik_pdv_a_8837df', 'Obveznik PDV-a') : this.$adminT('adminAll.nije_obveznik_pdv_a_627258', 'Nije obveznik PDV-a')
      const percentage = evt.vatPercentage != null ? ` · ${Number(evt.vatPercentage)}%` : ''

      return `${obligor}${percentage}`
    },
    isIssued(payout) {
      return String((payout && payout.status) || '').toLowerCase() === 'issued'
    },
    async setPayoutPaid(payout) {
      if (this.payoutReadStale || this.payingIds[payout.id] || payout.paidAt || !this.isIssued(payout)) return
      if (!window.confirm(this.$adminT('adminAll.potvr_ujete_da_je_ova_isplata_vec_izvrsena_ova_evidencija__03da69', 'Potvrđujete da je ova isplata već izvršena. Ova evidencija ne prenosi novac i ne izdaje račun. Oznaku nije moguće poništiti.'))) return
      this.$set(this.payingIds, payout.id, true)
      this.error = null
      try {
        await this.$apollo.mutate({ mutation: SET_ORGANIZER_PAYOUT_PAID, variables: { id: Number(payout.id), paid: true } })
        await this.refreshSettlementRecords()
      } catch (err) {
        this.error = err.message || this.adminAllNotice('Oznaku isplate nije moguće spremiti.')
      } finally {
        this.$delete(this.payingIds, payout.id)
      }
    },
    closeEventSearch() {
      setTimeout(() => {
        this.eventSearch.open = false
      }, 120)
    },
    closeTemplateSearch() {
      setTimeout(() => {
        this.templateSearch.open = false
      }, 120)
    },
    invalidateManualSearch() {
      const searches = [this.eventSearch, this.templateSearch]
      searches.forEach(state => {
        state.requestId++
        state.loading = false
        if (state.timer) clearTimeout(state.timer)
        state.timer = null
      })
    },
    debouncedSearch(kind, query) {
      const state = kind === 'event' ? this.eventSearch : this.templateSearch
      if (state.timer) {
        clearTimeout(state.timer)
      }
      state.timer = setTimeout(() => {
        if (kind === 'event') {
          this.searchEvents(query)
        } else {
          this.searchTemplates(query)
        }
      }, 300)
    },
    async searchEvents(query) {
      const requestId = ++this.eventSearch.requestId
      this.eventSearch.loading = false
      const term = (query || '').trim()
      this.eventSearch.error = null
      if (!term) {
        this.eventSearch.results = []

        return
      }
      if (!/^[0-9]+$/.test(term) && term.length < 2) {
        this.eventSearch.results = []

        return
      }

      const cacheKey = term.toLowerCase()
      if (this.eventSearchCache[cacheKey]) {
        this.eventSearch.results = this.eventSearchCache[cacheKey]

        return
      }

      this.eventSearch.loading = true
      try {
        const { data } = await this.$apollo.query({
          query: SEARCH_EVENTS_SUPERADMIN,
          variables: { q: term, limit: 20 },
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.eventSearch.requestId) return
        const results = (data && data.searchEventsSuperadmin) || []
        this.eventSearch.results = results
        this.eventSearchCache[cacheKey] = results
      } catch (err) {
        if (requestId !== this.eventSearch.requestId) return
        this.eventSearch.error = (err && err.message) || this.adminAllNotice('Failed to search events')
      } finally {
        if (requestId === this.eventSearch.requestId) {
          this.eventSearch.loading = false
        }
      }
    },
    async searchTemplates(query) {
      const requestId = ++this.templateSearch.requestId
      this.templateSearch.loading = false
      const term = (query || '').trim()
      this.templateSearch.error = null
      if (!term) {
        this.templateSearch.results = []

        return
      }
      if (!/^[0-9]+$/.test(term) && term.length < 2) {
        this.templateSearch.results = []

        return
      }

      const cacheKey = term.toLowerCase()
      if (this.templateSearchCache[cacheKey]) {
        this.templateSearch.results = this.templateSearchCache[cacheKey]

        return
      }

      this.templateSearch.loading = true
      try {
        const { data } = await this.$apollo.query({
          query: SEARCH_EVENT_TEMPLATES_SUPERADMIN,
          variables: { q: term, limit: 20 },
          fetchPolicy: 'network-only',
        })
        if (requestId !== this.templateSearch.requestId) return
        const results = (data && data.searchEventTemplatesSuperadmin) || []
        this.templateSearch.results = results
        this.templateSearchCache[cacheKey] = results
      } catch (err) {
        if (requestId !== this.templateSearch.requestId) return
        this.templateSearch.error = (err && err.message) || this.adminAllNotice('Failed to search templates')
      } finally {
        if (requestId === this.templateSearch.requestId) {
          this.templateSearch.loading = false
        }
      }
    },
    selectEvent(evt) {
      const id = evt && evt.id != null ? Number(evt.id) : null
      if (!id || !Number.isFinite(id)) return
      this.invalidateManualSearch()
      this.creationForm.eventId = id
      this.creationForm.eventTemplateId = null
      this.eventSearch.query =
        evt.manualOption ? `${id}` : `${evt.title}`
      this.eventSearch.open = false
    },
    selectTemplate(tpl) {
      const id = tpl && tpl.id != null ? Number(tpl.id) : null
      if (!id || !Number.isFinite(id)) return
      this.invalidateManualSearch()
      this.creationForm.eventTemplateId = id
      this.creationForm.eventId = null
      this.templateSearch.query =
        tpl.manualOption ? `${id}` : `${tpl.title}`
      this.templateSearch.open = false
    },
    buildFilter() {
      if (
        this.filters.fromDate &&
        this.filters.toDate &&
        this.filters.fromDate > this.filters.toDate
      ) {
        this.error = this.adminAllNotice('From date cannot be after To date.')

        return null
      }

      const normalizedEventFilter = (this.filters.eventTitle || '').trim()
      const eventIds = this.parseEventIds(normalizedEventFilter)

      return {
        settlementEventId: this.associationEventId || undefined,
        orgName: this.filters.orgName || undefined,
        eventTitle: eventIds ? undefined : normalizedEventFilter || undefined,
        eventId: eventIds && eventIds.length === 1 ? eventIds[0] : undefined,
        eventIds: eventIds && eventIds.length > 1 ? eventIds : undefined,
        eventTemplateTitle: this.filters.eventTemplateTitle || undefined,
        fromDate: this.filters.fromDate || undefined,
        toDate: this.filters.toDate || undefined,
      }
    },
    parseEventIds(value) {
      const term = (value || '').trim()
      if (!term) return null

      const tokens = term
        .split(/[\s,;]+/)
        .map(token => token.trim())
        .filter(Boolean)

      if (!tokens.length) return null
      if (!tokens.every(token => /^[0-9]+$/.test(token))) return null

      const ids = Array.from(
        new Set(
          tokens
            .map(token => Number(token))
            .filter(id => Number.isInteger(id) && id > 0),
        ),
      )

      return ids.length ? ids : null
    },
    validateCreationInput() {
      if (!(this.creationForm.eventId || this.creationForm.eventTemplateId)) {
        return this.adminAllNotice('Enter an Event ID or an Event Template ID.')
      }
      if (
        this.creationForm.fromDate &&
        this.creationForm.toDate &&
        this.creationForm.fromDate > this.creationForm.toDate
      ) {
        return this.adminAllNotice('"From date" cannot be after "To date".')
      }
      if (
        this.creationForm.eventWorkHours !== '' &&
        (!Number.isFinite(Number(this.creationForm.eventWorkHours)) || Number(this.creationForm.eventWorkHours) < 0)
      ) {
        return this.adminAllNotice('Event work hours must be a non-negative number.')
      }

      return null
    },
    normalizeEventWorkHours(value) {
      if (value === '' || value == null) return undefined
      const parsed = Number(value)
      if (!Number.isFinite(parsed) || parsed <= 0) return undefined

      return Number(parsed.toFixed(2))
    },
    async generateNewPayout(createInvoiceOverride = null) {
      if (this.generatingNew) return
      this.creationError = null
      this.creationSuccess = null

      const validationError = this.validateCreationInput()
      if (validationError) {
        this.creationError = validationError

        return
      }

      const eventId = this.creationForm.eventId
      const templateId = this.creationForm.eventTemplateId
      const createInvoice =
        createInvoiceOverride !== null ? createInvoiceOverride : this.creationForm.createInvoice
      this.generatingNew = true
      try {
        const requestPayload = {
          from: this.creationForm.fromDate || undefined,
          to: this.creationForm.toDate || undefined,
          createInvoice,
          eventWorkHours: this.normalizeEventWorkHours(this.creationForm.eventWorkHours),
        }
        let response
        if (templateId) {
          response = await downloadEventTemplatePayoutDocuments(templateId, requestPayload)
        } else {
          response = await downloadEventPayoutDocuments(eventId, requestPayload)
        }
        this.downloadBlobResponse(response, 'payout-documents.zip')
        this.creationSuccess = createInvoice
          ? this.adminAllNotice('Payout generated and downloaded.')
          : this.adminAllNotice('Preview generated and downloaded.')
        if (createInvoice) await this.refreshSettlementRecords()
        else await this.loadPayouts()
      } catch (err) {
        const message =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to generate payout')
        this.creationError = message
      } finally {
        this.generatingNew = false
      }
    },
    syncSendRecipientEmail() {
      if (!this.selectedSendOrganizationContext) {
        this.sendRecipientEmail = ''

        return
      }

      this.sendRecipientEmail = this.selectedSendOrganizationContext.email || ''
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim())
    },
    async sendSelectedPayoutDocs() {
      if (!this.downloadableSelectedIds.length) {
        this.error = this.adminAllNotice('Select at least one ISSUED payout before sending docs.')

        return
      }
      if (this.selectedSendOrganizationError) {
        this.error = this.adminAllNotice('Select issued payouts for a single organizer before sending documents.')

        return
      }

      const recipientEmail = (this.sendRecipientEmail || '').trim()
      if (!recipientEmail) {
        this.error = this.adminAllNotice('Recipient email is required before sending docs.')

        return
      }
      if (!this.isValidEmail(recipientEmail)) {
        this.error = this.adminAllNotice('Recipient email is invalid.')

        return
      }

      this.error = null
      this.sendSuccess = null
      this.sendingDocs = true
      try {
        const { data } = await sendOrganizerPayoutDocumentsByEmail(this.downloadableSelectedIds, recipientEmail)
        this.sendSuccess = (data && data.message) || this.adminAllNotice('Organizer payout documents sent to {{value1}}.', { value1: recipientEmail })
      } catch (err) {
        this.error = await this.extractHttpErrorMessage(err, this.adminAllNotice('Failed to send payout documents'))
      } finally {
        this.sendingDocs = false
      }
    },
    downloadBlobResponse(response, defaultName) {
      const blob = new Blob([response.data], { type: 'application/zip' })
      const disposition = response.headers['content-disposition']
      let filename = defaultName
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/)
        if (match && match[1]) {
          filename = match[1]
        }
      }
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    resetFilters() {
      this.associationEventId = null
      this.filters = {
        orgName: '',
        eventTitle: '',
        eventTemplateTitle: '',
        fromDate: '',
        toDate: '',
      }
      this.localSearch = ''
      this.page = 1
      this.loadPayouts()
    },
    formatDate(date) {
      if (!date) return '—'

      return moment(date).tz('Europe/Zagreb').format('DD.MM.YYYY')
    },
    formatAmount(amount) {
      if (amount == null) return '—'
      const num = Number(amount)

      return Number.isNaN(num) ? amount : `${num.toFixed(2)}`
    },
    formatPeriod(from, to, label) {
      if (label) return label
      if (!from && !to) return 'N/A'
      const fromStr = from ? moment(from).tz('Europe/Zagreb').format('DD.MM.YYYY') : '…'
      const toStr = to ? moment(to).tz('Europe/Zagreb').format('DD.MM.YYYY') : '…'

      return `${fromStr} - ${toStr}`
    },
    getOrgName(payout) {
      return payout && payout.organization && payout.organization.legalName
        ? payout.organization.legalName
        : '—'
    },
    getOrgType(payout) {
      return payout && payout.organization && payout.organization.type
        ? payout.organization.type
        : ''
    },
    getEventTitle(payout) {
      if (payout && payout.event && payout.event.title) {
        return payout.event.title
      }
      if (payout && payout.eventId) {
        return this.$adminT('adminAll.event_value1_6ce19e', 'Event #{{value1}}', { value1: payout.eventId })
      }

      return '—'
    },
    getTemplateTitle(payout) {
      if (
        payout &&
        payout.event &&
        payout.event.eventTemplate &&
        payout.event.eventTemplate.title
      ) {
        return payout.event.eventTemplate.title
      }

      return '—'
    },
    getTemplateId(payout) {
      if (payout && payout.event && payout.event.eventTemplate && payout.event.eventTemplate.id) {
        return payout.event.eventTemplate.id
      }
      if (payout && payout.metadata && payout.metadata.eventTemplateId) {
        return payout.metadata.eventTemplateId
      }

      return null
    },
    getEventId(payout) {
      if (!payout) return null
      if (payout.eventId) return payout.eventId
      if (payout.event && payout.event.id) return payout.event.id
      if (payout.metadata && payout.metadata.eventId) return payout.metadata.eventId

      return null
    },
    getTotalSoldAmount(payout) {
      const settlement = payout.settlement || (payout.metadata && payout.metadata.settlement)
      if (!settlement) return null

      return settlement.totalSoldAmount != null ? settlement.totalSoldAmount : null
    },
    getOrganizerNetAmount(payout) {
      const settlement = this.getSettlement(payout)
      if (!settlement) return null

      if (settlement.organizerNetAmount != null) return settlement.organizerNetAmount

      return null
    },
    getSettlement(payout) {
      return payout && (payout.settlement || (payout.metadata && payout.metadata.settlement))
        ? payout.settlement || payout.metadata.settlement
        : null
    },
    isPaymentEligible(payout) {
      const settlement = this.getSettlement(payout)
      if (settlement && settlement.payoutEligible !== undefined && settlement.payoutEligible !== null) {
        if (typeof settlement.payoutEligible === 'boolean') {
          return settlement.payoutEligible
        }
        if (settlement.payoutEligible === 'true') return true
        if (settlement.payoutEligible === 'false') return false
      }
      if (
        settlement &&
        settlement.requiresOrganizerInvoicePayment !== undefined &&
        settlement.requiresOrganizerInvoicePayment !== null
      ) {
        if (typeof settlement.requiresOrganizerInvoicePayment === 'boolean') {
          return !settlement.requiresOrganizerInvoicePayment
        }
        if (settlement.requiresOrganizerInvoicePayment === 'true') return false
        if (settlement.requiresOrganizerInvoicePayment === 'false') return true
      }
      const organizerNet = Number(this.getOrganizerNetAmount(payout))
      if (Number.isFinite(organizerNet)) {
        return organizerNet > 0
      }

      return true
    },
    toggleSelection(id) {
      if (this.selectedIds.includes(id)) {
        this.selectedIds = this.selectedIds.filter(val => val !== id)
      } else {
        this.selectedIds = [...this.selectedIds, id]
      }
    },
    toggleAll(event) {
      const checked = event.target.checked
      this.selectedIds = checked ? this.visiblePayouts.map(p => p.id) : []
    },
	    async downloadPayout(payout) {
	      if (this.downloadingId) return
	      const templateId = this.getTemplateId(payout)
	      const eventId = this.getEventId(payout)
	      this.downloadingId = payout.id
	      try {
	        let response
	        const status = payout && payout.status ? String(payout.status).toLowerCase() : ''
	        if (status === 'issued' && payout && payout.id) {
	          response = await downloadOrganizerPayoutDocuments([payout.id])
	        } else {
	          const from = payout.periodFrom || undefined
	          const to = payout.periodTo || undefined
          if (templateId) {
            response = await downloadEventTemplatePayoutDocuments(templateId, {
              from,
              to,
              createInvoice: false,
            })
          } else if (eventId) {
            response = await downloadEventPayoutDocuments(eventId, {
              from,
              to,
              createInvoice: false,
            })
          } else {
            this.error = this.adminAllNotice('Missing payout identifiers for download.')

            return
          }
        }
        const blob = new Blob([response.data], { type: 'application/zip' })
        const disposition = response.headers['content-disposition']
        let filename = 'payout-documents.zip'
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/)
          if (match && match[1]) {
            filename = match[1]
          }
        }
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        const message =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to download payout')
        this.error = message
      } finally {
        this.downloadingId = null
      }
    },
    async generatePaymentXml() {
      if (!this.paymentEligibleSelectedIds.length || this.generating) {
        if (!this.paymentEligibleSelectedIds.length) {
          this.error = this.adminAllNotice('Select at least one payout before generating XML.')
        }

        return
      }
      this.error = null
      this.paymentXmlWarning = null
      this.generating = true
      try {
        const response = await downloadOrganizerPayoutPaymentXml(this.paymentEligibleSelectedIds)
        const blob = new Blob([response.data], { type: 'application/xml' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'organizer-payouts.xml')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        const excluded = this.parsePaymentXmlExcludedHeader(response.headers)
        if (excluded.length) {
          this.paymentXmlWarning = excluded
        }
      } catch (err) {
        const message = await this.extractHttpErrorMessage(err, this.adminAllNotice('Failed to generate payment XML'))
        this.error = message
        this.paymentXmlWarning = null
      } finally {
        this.generating = false
      }
    },
    parsePaymentXmlExcludedHeader(headers) {
      if (!headers || !headers['x-organizer-payouts-excluded']) {
        return []
      }
      try {
        const encoded = headers['x-organizer-payouts-excluded']
        const payload = JSON.parse(window.atob(encoded))
        if (!payload || !Array.isArray(payload.exclusions)) {
          return []
        }

        return payload.exclusions
      } catch (error) {
        return []
      }
    },
    buildPaymentXmlExcludedMessage(exclusions) {
      const reasonLabel = reason => {
        if (reason === 'not_found') return this.$adminT('adminAll.not_found_094b76', 'not found')
        if (reason === 'eligibility_recalculation_failed') return this.$adminT('adminAll.eligibility_check_failed_daefe9', 'eligibility check failed')
        if (reason === 'invoice_payment_only') return this.$adminT('adminAll.invoice_payment_only_91a1c3', 'invoice payment only')
        if (reason === 'missing_stored_amount') return this.$adminT('adminAll.missing_stored_payout_amount_265d98', 'missing stored payout amount')
        if (reason === 'non_positive_amount') return this.$adminT('adminAll.non_positive_payout_amount_f09f43', 'non-positive payout amount')

        return reason || this.$adminT('adminAll.excluded_7471fc', 'excluded')
      }
      const previewItems = exclusions.slice(0, 5).map(item => `#${item.payoutId} (${reasonLabel(item.reason)})`)
      const suffix = exclusions.length > 5 ? this.$adminT('adminAll.and_value1_more_d69356', ', and {{value1}} more', { value1: exclusions.length - 5 }) : ''

      return this.$adminT('adminAll.payment_xml_generated_with_exclusions_value1_value2_0473a3', 'Payment XML generated with exclusions: {{value1}}{{value2}}.', { value1: previewItems.join(', '), value2: suffix })
    },
    async extractHttpErrorMessage(err, fallbackMessage) {
      const blobData = err && err.response && err.response.data
      if (blobData instanceof Blob) {
        try {
          const raw = await blobData.text()
          const parsed = JSON.parse(raw)
          if (parsed && parsed.message) {
            return parsed.message
          }
        } catch (error) {
          // Ignore blob parsing errors and fallback to generic message handling.
        }
      }
      const message =
        (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || fallbackMessage

      return message
    },
    async downloadSelectedPayoutDocs() {
      if (!this.downloadableSelectedIds.length || this.generating) {
        if (!this.downloadableSelectedIds.length) {
          this.error = this.adminAllNotice('Select at least one ISSUED payout before downloading docs.')
        }

        return
      }
      this.generating = true
      try {
        const response = await downloadOrganizerPayoutDocuments(this.downloadableSelectedIds)
        const blob = new Blob([response.data], { type: 'application/zip' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'organizer-payout-docs.zip')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        const message =
          (err && err.response && err.response.data && err.response.data.message) ||
          err.message ||
          this.adminAllNotice('Failed to download payout documents')
        this.error = message
      } finally {
        this.generating = false
      }
    },
    resetCreationForm() {
      this.invalidateManualSearch()
      this.creationForm = {
        eventId: null,
        eventTemplateId: null,
        fromDate: '',
        toDate: '',
        eventWorkHours: '',
        createInvoice: true,
      }
      this.creationError = null
      this.creationSuccess = null
      this.sendSuccess = null
      this.localSearch = ''
      this.eventSearch.query = ''
      this.eventSearch.results = []
      this.eventSearch.error = null
      this.eventSearch.open = false
      this.templateSearch.query = ''
      this.templateSearch.results = []
      this.templateSearch.error = null
      this.templateSearch.open = false
    },
  },
}
</script>

<style scoped>
.settlement-editor, .settlement-history { margin-top: 16px; }
.settlement-editor label { display: block; margin: 12px 0; }
.settlement-editor textarea { display: block; width: 100%; border: 1px solid #cbd5e1; min-height: 70px; }
.settlement-history pre { white-space: pre-wrap; overflow-wrap: anywhere; }
.settlement-table .link-button { display: block; margin-top: 8px; }
.pager button, .settlement-editor button { padding: 8px 12px; color: #2563eb; }

.payouts-page {
  width: 100%;
  min-width: 0;
  padding: 28px;
  min-height: 100vh;
  color: #111827;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 1280px;
  margin: 0 auto;
}
.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: #2563eb;
  font-weight: 700;
  padding: 0;
  margin-bottom: 12px;
  cursor: pointer;
  text-decoration: none;
}
.breadcrumb-icon {
  font-size: 16px;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 20px;
}
.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}
h1 {
  margin: 4px 0;
  font-size: 30px;
  color: #0f172a;
}
.lede {
  margin: 6px 0 0;
  color: #4b5563;
  max-width: 580px;
}
.summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  min-width: 110px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
  text-transform: uppercase;
  font-size: 11px;
  color: #6b7280;
}
.summary-count {
  font-weight: 800;
  font-size: 26px;
  color: #0f172a;
  line-height: 1.1;
}
.summary-label {
  margin-top: 2px;
}
.card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.filters {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  align-items: end;
}
.filter-row.secondary-row {
  grid-template-columns: 1fr 260px;
}
.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.filter label {
  font-weight: 700;
  font-size: 12px;
  color: #334155;
}
.filter input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.filter input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}
.filter.wide {
  grid-column: span 1;
}
.actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}
.creation-card {
  margin-bottom: 16px;
}
.settlement-card {
  margin-bottom: 16px;
}
.settlement-card h3 {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
}
.settlement-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
}
.settlement-filters {
  margin-top: 20px;
  margin-bottom: 16px;
}
.settlement-select {
  width: 100%;
  min-width: 180px;
  padding: 9px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
  appearance: auto;
}
.settlement-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}
.settlement-select.is-paid {
  background: #ecfdf3;
  border-color: #86efac;
  color: #15803d;
}
.settlement-select.is-issued {
  background: #dbeafe;
  border-color: #93c5fd;
  color: #1e40af;
}
.settlement-select.is-pending {
  background: #fff7ed;
  border-color: #fdba74;
  color: #c2410c;
}
.settlement-select.is-failed {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #b91c1c;
}
.settlement-select.is-draft {
  background: #fef9c3;
  border-color: #fde047;
  color: #854d0e;
}
.settlement-card .settlement-table-wrapper {
  max-height: min(60vh, 560px);
}
.settlement-table {
  min-width: 1080px;
}
.settlement-help { margin-top: 12px; color: #475569; font-size: 13px; max-width: 75ch; }
.settlement-help summary { cursor: pointer; color: #2563eb; }
.settlement-help p { margin: 8px 0; }
.settlement-filters {
  grid-template-columns: minmax(150px, 1fr) minmax(150px, 1fr) minmax(230px, 1.4fr) minmax(285px, 1.8fr);
  gap: 16px;
  margin-bottom: 12px;
}
.settlement-filters .filter { min-width: 0; }
.settlement-filters input, .settlement-filters select {
  min-width: 0;
  height: 44px;
  box-sizing: border-box;
  text-transform: none;
  font-weight: 400;
  letter-spacing: normal;
}
.settlement-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 16px; }
.settlement-load-button { margin: 0; text-transform: none; letter-spacing: normal; }
.settlement-results { margin-bottom: 10px; }
.settlement-table-wrapper .settlement-table { table-layout: fixed; min-width: 1040px; }
.settlement-table .settlement-event-col { width: 21%; }
.settlement-table .settlement-organizer-col { width: 21%; }
.settlement-table .settlement-payout-col { width: 19%; }
.settlement-table .settlement-outside-col { width: 25%; }
.settlement-table .settlement-actions-col { width: 14%; }
.settlement-card .settlement-table td { vertical-align: top; overflow-wrap: anywhere; }
.settlement-card .settlement-table th { text-transform: none; letter-spacing: normal; }
.settlement-table .link-button { text-align: left; line-height: 1.5; padding: 4px 0; }
.settlement-open-button {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 10px;
  min-height: 40px;
  cursor: pointer;
}
.settlement-open-button:hover { background: #eff6ff; border-color: #2563eb; }
.settlement-card button:focus-visible, .settlement-help summary:focus-visible, .settlement-table-wrapper:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 3px;
}
@media (max-width: 1100px) {
  .settlement-filters { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
  .settlement-filters { grid-template-columns: minmax(0, 1fr); }
  .settlement-toolbar { align-items: stretch; flex-direction: column; }
}

.nowrap {
  white-space: nowrap;
}
.row-action-button {
  white-space: nowrap;
}
.paid-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.link-button {
  border: 0;
  background: none;
  padding: 0;
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.link-button:disabled {
  color: #94a3b8;
  cursor: default;
}
.combo-section {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}
.combo-item-sub {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
}
.creation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.creation-card h3 {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
}
.helper-text {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
}
.field-helper {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 12px;
}
.creation-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 20px;
}
.creation-grid label {
  display: block;
  font-weight: 700;
  margin-bottom: 6px;
  color: #334155;
  font-size: 12px;
}
.creation-grid input[type='text'],
.creation-grid input[type='number'],
.creation-grid input[type='date'] {
  width: 100%;
  padding: 11px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
}
.creation-grid input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}
.combo {
  position: relative;
  width: 100%;
}
.combo-input {
  position: relative;
}
.combo-input input {
  width: 100%;
  padding: 11px 36px 11px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
}
.combo-input input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35);
}
.chevron-icon {
  position: absolute;
  right: 10px;
  top: 0;
  bottom: 0;
  margin: auto;
  height: fit-content;
}
.combo-menu {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  z-index: 50;
  max-height: 260px;
  overflow: auto;
  padding: 6px;
}
.combo-item {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  text-align: left;
  border: none;
  background: transparent;
  padding: 10px 10px;
  border-radius: 8px;
  cursor: pointer;
  color: #0f172a;
}
.combo-item:hover {
  background: #f8fafc;
}
.combo-item-title {
  font-weight: 600;
}
.combo-item-meta {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
}
.combo-empty {
  padding: 10px 10px;
  color: #64748b;
  font-size: 13px;
}
.creation-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 20px 0 16px;
}
.creation-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.alert {
  padding: 10px 12px;
  border-radius: 10px;
  margin-top: 12px;
  border: 1px solid transparent;
}
.alert.error {
  background: #fef2f2 !important;
  color: #b91c1c;
  border-color: #fecdd3;
}
.alert.success {
  background: #ecfdf3;
  color: #15803d;
  border-color: #bbf7d0;
}
.alert.warning {
  background: #fffbeb;
  color: #92400e;
  border-color: #fde68a;
}
.empty {
  color: #6b7280;
  padding: 20px;
  text-align: center;
}
.results-panel {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.table-wrapper {
  overflow: auto;
  max-height: min(70vh, 720px);
  -webkit-overflow-scrolling: touch;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.results-panel .table-wrapper {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: none;
  margin-bottom: 0;
}
.payouts-table {
  width: 100%;
  min-width: 1180px;
  border-collapse: separate;
  border-spacing: 0;
}
.payouts-table th,
.payouts-table td {
  padding: 14px 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
}
.payouts-table th {
  font-weight: 800;
  font-size: 12px;
  color: #64748b;
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 2;
  background-clip: padding-box;
  box-shadow: inset 0 -1px 0 #d1d5db;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.payouts-table--loading td {
  padding-top: 16px;
  padding-bottom: 16px;
}
.table-skeleton-line {
  width: 100%;
  height: 14px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: organizer-payouts-shimmer 1.4s ease-in-out infinite;
}
.table-skeleton-line--header {
  height: 12px;
}
.payouts-table tbody tr:hover {
  background: #f9fbff;
}
.payouts-table input[type='checkbox'] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.checkbox-col {
  width: 48px;
}
.align-center {
  text-align: center;
}
.align-right {
  text-align: right;
}
.cell-title {
  font-weight: 700;
  color: #0f172a;
}
.cell-subtitle {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
}
.muted {
  color: #6b7280;
}
.emphasis {
  font-weight: 800;
  color: #0f172a;
}
.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  background: #eef2ff;
  color: #4338ca;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.pill.issued {
  background: #dbeafe;
  color: #1e40af;
}
.pill.draft-estimate,
.pill.draft,
.pill.estimate {
  background: #fef9c3;
  color: #854d0e;
}
.pill.paid {
  background: #ecfdf3;
  color: #15803d;
}
.pill.pending {
  background: #fff7ed;
  color: #c2410c;
}
.pill.failed {
  background: #fef2f2 !important;
  color: #b91c1c;
}
.table-footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 0 0 8px 8px;
}
.table-footer-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}
.footer-actions {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.pager {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.pager-text {
  color: #64748b;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
}
.selection-hint {
  color: #6b7280;
  font-size: 14px;
}
.pager-button,
.footer-action-button {
  white-space: normal;
}
.email-send-panel {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: min(100%, 340px);
  justify-content: flex-start;
}
.email-send-panel input {
  min-width: 0;
  max-width: 360px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
}
.email-send-panel input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}
.footer-alert {
  margin: 10px 0 0;
}
.main-button.primary {
  background: #2563eb;
  color: #fff;
  border-radius: 6px;
}
.main-button.secondary {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #2563eb;
  border-radius: 6px;
}
.main-button.ghost {
  background: transparent;
  color: #2563eb;
  border-radius: 6px;
}
.main-button.primary[disabled],
.main-button.secondary[disabled],
.main-button.ghost[disabled] {
  opacity: 0.6;
}
@media print {
  .breadcrumb {
    display: none !important;
  }

  .table-wrapper {
    max-height: none !important;
    height: auto !important;
    overflow: visible !important;
  }

  .payouts-table th {
    position: static;
    box-shadow: none;
  }

  .payouts-table thead,
  .payouts-table tfoot {
    display: table-row-group !important;
  }

  .payouts-table tr,
  .payouts-table td,
  .payouts-table th {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
}
@keyframes organizer-payouts-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
@media (max-width: 960px) {
  .payouts-page {
    padding: 20px;
  }
  .card {
    padding: 20px;
  }
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .filter-row.secondary-row {
    grid-template-columns: 1fr;
  }
  .creation-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .creation-grid {
    grid-template-columns: 1fr;
  }
  .actions {
    justify-content: flex-start;
  }
  .table-footer {
    padding: 14px;
  }
  .table-footer-top {
    align-items: stretch;
  }
  .footer-actions {
    flex-direction: column;
  }
  .footer-action-button {
    width: 100%;
  }
  .email-send-panel {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
@media (max-width: 1180px) and (orientation: landscape) {
  .table-footer-top,
  .footer-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
@media (max-width: 640px) {
  .payouts-page {
    padding: 14px;
  }
  .card {
    padding: 16px;
  }
  .table-footer {
    padding: 12px;
  }
  .table-footer-top {
    padding-bottom: 10px;
  }
  .pager {
    justify-content: stretch;
    width: 100%;
  }
  .pager-button,
  .pager-text {
    flex: 1 1 100%;
    text-align: center;
  }
  .pager-button {
    width: 100%;
  }
  .selection-hint {
    display: block;
  }
  .email-send-panel {
    flex-direction: column;
    align-items: stretch;
  }
  .email-send-panel input {
    max-width: none;
  }
  .footer-action-button {
    width: 100%;
  }
}
</style>

<style>
@media print {
  body.admin-report-print-layout .header,
  body.admin-report-print-layout .footer,
  body.admin-report-print-layout .application-toolbar,
  body.admin-report-print-layout .application-toolbar-zkm,
  body.admin-report-print-layout .back-button-zkm {
    display: none !important;
  }
}
</style>
